import type { ReportVersionDto } from '@/api/WhisApi/generated';
import { useAppTranslation } from '@/hooks/useAppTranslation';
import { formatDateForLocale } from '@/screens/ReportDetail/reportDetail.utils';
import { Ionicons } from '@expo/vector-icons';
import { Pressable } from 'react-native';
import { SizableText, XStack, YStack, useTheme } from 'tamagui';
import { formatVersionNumber } from './reportDetailVersions.utils';

interface ReportVersionCardProps {
  version: ReportVersionDto;
  onPress?: () => void;
  isSelected?: boolean;
}

export const ReportVersionCard = ({
  version,
  onPress,
  isSelected = false,
}: ReportVersionCardProps): React.ReactElement => {
  const { t, language } = useAppTranslation();
  const theme = useTheme();

  const formattedVersion = formatVersionNumber(version.versionNumber);
  const formattedDate = formatDateForLocale(version.createdAt, language);
  const iconColor = theme.color?.val ?? '#27282A';
  const inactiveIconColor = theme.secondary75?.val ?? iconColor;

  return (
    <Pressable onPress={onPress} disabled={!onPress} accessibilityRole="button">
      <XStack
        alignItems="center"
        justifyContent="space-between"
        gap="$2"
        backgroundColor="$tertiary50"
        padding="$3"
      >
        <YStack flex={1} gap="$1" alignItems="flex-start">
          <SizableText size="$1" fontFamily="$bold">
            {t('reportDetail.versions.versionLabel', {
              version: formattedVersion,
            })}
          </SizableText>
          {formattedDate && (
            <SizableText size="$2" color="$secondary75">
              {formattedDate}
            </SizableText>
          )}
          {version.versionNote && (
            <SizableText size="$2" color="$secondary">
              {version.versionNote}
            </SizableText>
          )}
        </YStack>
        <Ionicons
          name={isSelected ? 'checkmark-circle' : 'ellipse-outline'}
          size={20}
          color={isSelected ? iconColor : inactiveIconColor}
        />
      </XStack>
    </Pressable>
  );
};
